import React, { useCallback, useMemo, useState, Fragment, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import EventLabel from '../../components/EventLabel/EventLabel';
import { Dialog, Transition } from '@headlessui/react';
import { basedAxios } from '../../store/Store';
import axios from 'axios';
import cx from 'classnames';
import './ticket.css';
import QRCode from 'react-qr-code';
import { format } from 'date-fns';
import { InsertInvitation, EventBusy } from '@material-ui/icons';
import SystemLabel from '../../components/systemlabel/SystemLabel';
import { HubConnection, HubConnectionBuilder } from '@microsoft/signalr';
import { LoadSpinner } from '../../components/loadspinner/LoadSpinner';

interface TicketsProps {
  orders: any;
  activePersonNo: number;
  breakouts: any;
  updateOrderRow: any;
  preview: boolean;
  settings: {
    showAsQr: boolean;
    ticketEventId?: number;
  };
}

type Ticket = {
  orderRowId: number;
  ticketIndex: number;
  eventId: number;
  name: string;
  startDate: number;
  endDate: number;
  used: boolean;
  encryptedQrData?: string; //only for Qr-code tickets
};

const getApiBaseUrl = () => {
  const hostname = window.location.hostname.toLowerCase();
  if (hostname.includes('localhost')) {
    return 'https://localhost:50259';
  } else if (hostname.includes('dev')) {
    return 'https://dev.internal.jirango.com';
  } else {
    return 'https://internal.jirango.com';
  }
};

const API_BASE_URL = getApiBaseUrl();
const ticketAxios = axios.create({
  baseURL: API_BASE_URL,
});

function Tickets(props: any) {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedTicket, setSelectedTicket] = useState<number | null>(null);
  const [confirmed, setConfirmed] = useState<boolean>(false);
  const [connection, setConnection] = useState<HubConnection | null>(null);
  const [encryptedTickets, setEncryptedTickets] = useState<{ [key: string]: string }>({});

  const { orders, activePersonNo, breakouts, updateOrderRow, preview, settings, eventId: parentEventId } = props;
  const { showAsQr, activity } = settings;

  useEffect(() => {
    if (!showAsQr || !orders?.[activePersonNo]?.orderId) return;

    const fetchEncryptedTickets = async () => {
      try {
        const response = await ticketAxios.get(`/${activity || 0}/ticket/getEncryptedTickets/${orders[activePersonNo].orderId}`);
        const encrypted: { [key: string]: string } = {};
        response.data.forEach((ticket: any) => {
          encrypted[`${ticket.orderRowId}_${ticket.ticketIndex}`] = ticket.encryptedQrData;
        });
        setEncryptedTickets(encrypted);
      } catch (error) {
        console.error('Failed to fetch encrypted tickets:', error);
      }
    };

    fetchEncryptedTickets();
  }, [orders, activePersonNo, showAsQr]);

  const tickets = useMemo(() => {
    const orderRows = orders?.[activePersonNo]?.orderRow ?? [];
    const ticketSources = breakouts?.filter(
      (breakout: any) =>
        breakout.ticket &&
        orderRows?.some((orderRow: any) => orderRow?.eventId === breakout?.eventId) &&
        (activity === 0 || activity === null || breakout.eventId === activity)
    );

    return ticketSources?.flatMap((source: any) => {
      const breakout = breakouts?.find((breakout: any) => breakout.eventId === source?.eventId);
      const orderRow = orderRows?.find((orderRow: any) => orderRow.eventId === source?.eventId);

      const name = breakout?.name || 'No name';
      const quantity = orderRow.quantity;
      const used = orderRow.usedTickets ?? 0;
      const eventId = source.eventId;
      const startDate = source.startDateTime;
      const endDate = source.endDateTime;

      const result: Ticket[] = [];
      for (let i = 0; i < quantity; i++) {
        const isUsed = i < used;
        const ticket: Ticket = {
          orderRowId: orderRow?.orderRowId,
          ticketIndex: i,
          eventId,
          name,
          startDate,
          endDate,
          used: isUsed,
        };

        if (showAsQr) {
          ticket.encryptedQrData = encryptedTickets[`${orderRow.orderRowId}_${i}`] || '';
        }
        result.push(ticket);
      }

      return result.sort((a, b) => (a.used === b.used ? 0 : a.used ? 1 : -1));
    });
  }, [orders, activePersonNo, breakouts, encryptedTickets, showAsQr]);

  const handleClose = () => {
    setIsOpen(false);
    setSelectedTicket(null);
  };
  const handleOpen = () => setIsOpen(true);

  const confirmUsage = (id: number) => {
    handleOpen();
    setSelectedTicket(id);
  };

  const saveOrderRow = async (orderRow: any) => {
    try {
      await basedAxios.put(`./orderRow/useticket/${orderRow.orderRowId}`, orderRow);
    } catch (error) {
      console.error(error);
    }
  };

  const useTicket = useCallback(() => {
    const orderRow = orders?.[activePersonNo]?.orderRow?.find((orderRow: any) => orderRow.orderRowId === selectedTicket);

    if (orderRow.usedTickets === orderRow.quantity) return;

    setConfirmed(true);

    const updatedOrderRow = {
      ...orderRow,
      usedTickets: (orderRow.usedTickets ?? 0) + 1,
    };

    updateOrderRow(updatedOrderRow);

    setSelectedTicket(null);
    setIsOpen(false);

    saveOrderRow(updatedOrderRow);

    setConfirmed(false);
  }, [orders, selectedTicket]);

  const calculateQrSize = () => {
    const screenWidth = window.innerWidth;
    if (screenWidth < 640) return 90;
    if (screenWidth < 768) return 100;
    if (screenWidth < 1024) return 115;
    if (screenWidth < 1280) return 120;
    if (screenWidth < 1920) return 125;
    return 125;
  };

  if (tickets?.length === 0) return null;

  return (
    <>
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={handleClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-fit max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900 text-center">
                    <EventLabel name="UseTicket" groupName="CMS.Tickets" />
                  </Dialog.Title>

                  <div className="mt-4 flex gap-x-2 justify-center">
                    <button
                      className="inline-flex justify-center rounded-md border border-transparent bg-neutral-200 px-4 py-2 text-sm font-medium text-gray-900 focus:outline-none focus-visible:ring-2 "
                      onClick={handleClose}
                    >
                      <EventLabel name="Cancel" groupName="CMS.Tickets" />
                    </button>
                    <button
                      className="inline-flex justify-center rounded-md border border-transparent bg-[teal] px-4 py-2 text-sm font-medium text-white focus:outline-none focus-visible:ring-2 "
                      onClick={useTicket}
                    >
                      {confirmed ? (
                        <>
                          <i className="ri ri-check-line mr-1" /> <EventLabel name="TicketIsUsed" groupName="CMS.Tickets" />{' '}
                        </>
                      ) : (
                        <EventLabel name="Use" groupName="CMS.Tickets" />
                      )}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      <div className="w-full">
        <h3 className="mb-2">
          <EventLabel name="Tickets" groupName="CMS.Tickets" />
        </h3>
        <p>
          <EventLabel name="Quantity" groupName="CMS.Tickets" />
          {': '} {tickets?.filter((ticket: Ticket) => !ticket.used).length}
        </p>
        <br />
        <div className="grid xl:grid-cols-3 lg:grid-cols-2 md:grid-cols-2 sm:grid-cols-2 grid-cols-1 gap-x-4 gap-y-4">
          {tickets?.map((ticket: Ticket, index: number) => (
            <div
              key={`${ticket.orderRowId}_${ticket.ticketIndex}`}
              className={cx('h-full col-span-3 flex flex-row rounded-l-lg shadow-sm', ticket.used ? 'opacity-50' : 'opacity-100')}
            >
              {showAsQr ? (
                <>
                  {/* Left side: QR Code */}
                  <div className="rounded-l-lg bg-white items-center flex justify-center pl-2">
                    <QRCode value={ticket.encryptedQrData} className="rounded-md" size={calculateQrSize()} />
                  </div>

                  {/* Right side: Name */}
                  <div className="flex flex-col gap-2 w-full h-full rounded-r-lg bg-white p-2">
                    <p className="text-xs md:text-lg lg:text-xl font-semibold">{ticket.name}</p>
                    <p className="text-[0.65rem] md:text-[0.85rem] lg:text-lg flex gap-1">
                      <SystemLabel name="TicketIndexDisplay" /> {ticket.ticketIndex + 1}
                    </p>
                    <span className="flex flex-col">
                      <p className="mt-1 text-[0.65rem] sm:text-xs md:text-sm lg:text-sm flex gap-1">
                        <SystemLabel name="TicketStartDate" /> {format(new Date(ticket.startDate), 'yyyy-MM-dd HH:ss')}
                      </p>
                    </span>
                    <span className="flex flex-col">
                      <p className="mt-1 text-[0.65rem] sm:text-xs md:text-sm lg:text-sm flex gap-1">
                        <SystemLabel name="TicketEndDate" /> {format(new Date(ticket.endDate), 'yyyy-MM-dd HH:ss')}
                      </p>
                    </span>
                  </div>
                </>
              ) : (
                <div className={cx('ticket xl:grid-span-4 lg:grid-span-2 grid-span-1', ticket.used ? 'opacity-50' : 'opacity-100')}>
                  <div className="top --flex-column">
                    <div className="bandname -bold">{ticket.name}</div>
                  </div>
                  <div className="rip">
                    <hr />
                  </div>
                  <div className="bottom --flex-row-j!sb">
                    <button
                      disabled={ticket.used}
                      className="cms-button w-fit"
                      style={{ fontSize: 14, width: 'fit-content' }}
                      onClick={() => confirmUsage(ticket.orderRowId)}
                    >
                      {ticket.used ? <EventLabel name="TicketIsUsed" groupName="CMS.Tickets" /> : <EventLabel name="UseTicket" groupName="CMS.Tickets" />}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

export default observer(Tickets);
